import React, { useState, useEffect } from "react";
import { api } from "../../App";
import { getApiPrefix } from "../../utils/apiHelper";
import { toast } from "sonner";
import {
  Plus, Trash2, GripVertical, Save, Layers,
  ChevronDown, ChevronUp, Settings2, X, Equal, Minus
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

// Utility: extract unique 3-digit prefixes from conti list
function getCodiciPrefisso(conti) {
  if (!conti || conti.length === 0) return [];
  const map = {};
  for (const c of conti) {
    const cod = String(c.codice || c.conto_codice || "");
    if (cod.length >= 3) {
      const prefix = cod.substring(0, 3);
      if (!map[prefix]) {
        const nome = c.nome || c.conto_nome || "";
        map[prefix] = { codice: prefix, nome: nome.length > 25 ? nome.substring(0, 25) + "..." : nome, count: 1 };
      } else {
        map[prefix].count++;
      }
    }
  }
  return Object.values(map).sort((a, b) => a.codice.localeCompare(b.codice));
}

function LayoutRowEditor({ row, idx, total, contiPrefissi, contiDisponibili, categorieDisponibili, onChange, onRemove, onMove }) {
  const [showCodici, setShowCodici] = useState(false);
  const [showCategorie, setShowCategorie] = useState(false);

  const bgColor = row.tipo === "ricavi" ? "bg-green-50 border-green-200" :
                  row.tipo === "gruppo" ? "bg-red-50 border-red-200" :
                  "bg-blue-50 border-blue-200";

  const icon = row.tipo === "ricavi" ? <Plus className="w-3.5 h-3.5 text-green-600" /> :
               row.tipo === "gruppo" ? <Minus className="w-3.5 h-3.5 text-red-600" /> :
               <Equal className="w-3.5 h-3.5 text-blue-600" />;

  const toggleCodice = (cod) => {
    const current = row.codici || [];
    if (current.includes(cod)) {
      onChange("codici", current.filter(c => c !== cod));
    } else {
      onChange("codici", [...current, cod]);
    }
  };

  const toggleCategoria = (catId) => {
    const current = row.categoria_ids || [];
    if (current.includes(catId)) {
      onChange("categoria_ids", current.filter(c => c !== catId));
    } else {
      onChange("categoria_ids", [...current, catId]);
    }
  };

  // Fuzzy-match categorie sul nome della riga. Utile per pre-selezionare
  // automaticamente "Costo del personale" per una riga "Costo del Personale".
  const suggerisciCategorie = () => {
    if (!categorieDisponibili || categorieDisponibili.length === 0) return;
    const normalize = (s) => String(s || "").toLowerCase().trim()
      .replace(/[àáâã]/g, "a").replace(/[èéêë]/g, "e")
      .replace(/[ìí]/g, "i").replace(/[òó]/g, "o").replace(/[ùú]/g, "u")
      .replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ");
    const rowTokens = normalize(row.nome).split(" ").filter(t => t.length >= 3);
    if (rowTokens.length === 0) {
      toast.error("Il nome della riga è troppo corto per il matching automatico");
      return;
    }
    const matched = categorieDisponibili.filter(c => {
      const catTokens = normalize(c.nome);
      return rowTokens.some(t => catTokens.includes(t));
    }).map(c => c.id);
    if (matched.length === 0) {
      toast.error(`Nessuna categoria trovata con il nome "${row.nome}"`);
      return;
    }
    onChange("categoria_ids", matched);
    toast.success(`${matched.length} categoria/e associate a "${row.nome}"`);
    setShowCategorie(true);
  };

  const nConfigurazioni = (row.codici || []).length + (row.categoria_ids || []).length;
  const isEmptyGruppo = (row.tipo === "ricavi" || row.tipo === "gruppo") && nConfigurazioni === 0;

  return (
    <div className={`flex items-start gap-2 p-2.5 rounded-lg border ${bgColor}`} data-testid={`layout-row-${idx}`}>
      <div className="flex flex-col items-center gap-0.5 pt-1">
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => onMove(-1)} disabled={idx === 0}>
          <ChevronUp className="w-3 h-3" />
        </Button>
        <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
        <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => onMove(1)} disabled={idx === total - 1}>
          <ChevronDown className="w-3 h-3" />
        </Button>
      </div>
      <div className="pt-2">{icon}</div>
      <div className="flex-1 space-y-1.5">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] shrink-0">
            {row.tipo === "ricavi" ? "RICAVI" : row.tipo === "gruppo" ? "COSTI" : "MARGINE"}
          </Badge>
          <Input
            value={row.nome}
            onChange={e => onChange("nome", e.target.value)}
            className="h-7 text-sm font-medium"
            placeholder="Nome riga"
          />
        </div>
        {(row.tipo === "ricavi" || row.tipo === "gruppo") && (
          <div className="space-y-1.5">
            <div>
              <button onClick={() => setShowCodici(!showCodici)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid={`toggle-codici-${idx}`}>
                <Settings2 className="w-3 h-3" />
                {(row.codici || []).length > 0 ? `Prefissi: ${(row.codici || []).join(", ")}` : "Seleziona prefissi conto"}
                {showCodici ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {showCodici && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {contiPrefissi.map(p => (
                    <Button
                      key={p.codice}
                      variant={(row.codici || []).includes(p.codice) ? "default" : "outline"}
                      size="sm"
                      className="h-6 text-[10px] px-2"
                      onClick={() => toggleCodice(p.codice)}
                    >
                      {p.codice} {p.nome}
                    </Button>
                  ))}
                </div>
              )}
            </div>
            {categorieDisponibili && categorieDisponibili.length > 0 && (
              <div>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowCategorie(!showCategorie)} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid={`toggle-categorie-${idx}`}>
                    <Layers className="w-3 h-3" />
                    {(row.categoria_ids || []).length > 0
                      ? `Categorie: ${(row.categoria_ids || []).length} selezionate`
                      : "Seleziona categorie (consigliato)"}
                    {showCategorie ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={suggerisciCategorie}
                    className="text-[10px] text-blue-600 hover:text-blue-800 underline"
                    data-testid={`suggest-categorie-${idx}`}
                    title={`Cerca categorie con nome simile a "${row.nome}"`}
                  >
                    Suggerisci dal nome
                  </button>
                </div>
                {showCategorie && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {categorieDisponibili.map(c => (
                      <Button
                        key={c.id}
                        variant={(row.categoria_ids || []).includes(c.id) ? "default" : "outline"}
                        size="sm"
                        className="h-6 text-[10px] px-2"
                        onClick={() => toggleCategoria(c.id)}
                        data-testid={`categoria-toggle-${c.codice}`}
                      >
                        {c.nome}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {isEmptyGruppo && (
              <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`warning-empty-${idx}`}>
                ⚠ Nessun prefisso o categoria selezionato: questa riga non includerà alcun conto nel report.
              </div>
            )}
          </div>
        )}
      </div>
      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onRemove}>
        <X className="w-3.5 h-3.5 text-muted-foreground" />
      </Button>
    </div>
  );
}

function LayoutPreview({ rows }) {
  if (!rows || rows.length === 0) return null;

  const describeRow = (row) => {
    const parts = [];
    if ((row.codici || []).length > 0) parts.push(`codici: ${row.codici.join(", ")}`);
    if ((row.categoria_ids || []).length > 0) parts.push(`${row.categoria_ids.length} categorie`);
    return parts.join(" • ");
  };

  return (
    <div className="border rounded-lg p-3 bg-muted/30" data-testid="layout-preview">
      <p className="text-xs font-semibold text-muted-foreground mb-2">Anteprima Struttura</p>
      <div className="space-y-1">
        {rows.map((row, idx) => {
          if (row.tipo === "ricavi") {
            return (
              <div key={`preview-${row.nome}-${idx}`} className="flex items-center gap-2 text-sm text-green-700 font-medium pl-0">
                <Plus className="w-3 h-3" /> {row.nome} <span className="text-[10px] text-muted-foreground">({describeRow(row) || "—"})</span>
              </div>
            );
          }
          if (row.tipo === "gruppo") {
            return (
              <div key={`preview-${row.nome}-${idx}`} className="flex items-center gap-2 text-sm text-red-700 pl-4">
                <Minus className="w-3 h-3" /> {row.nome} <span className="text-[10px] text-muted-foreground">({describeRow(row) || "—"})</span>
              </div>
            );
          }
          return (
            <div key={`preview-${row.nome}-${idx}`} className="flex items-center gap-2 text-sm text-blue-700 font-bold border-t border-blue-200 pt-1 mt-1">
              <Equal className="w-3 h-3" /> {row.nome}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function LayoutEditorDialog({ open, onClose, contiDisponibili, categorieDisponibili, onSaved }) {
  const [layouts, setLayouts] = useState([]);
  const [selectedLayoutId, setSelectedLayoutId] = useState(null);
  const [nome, setNome] = useState("Conto Economico a Scalare");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) fetchLayouts();
  }, [open]);

  const fetchLayouts = async () => {
    try {
      const prefix = getApiPrefix();
      const res = await api.get(`${prefix}/bilancio-layouts`);
      setLayouts(res.data || []);
      const active = (res.data || []).find(l => l.attivo);
      if (active) {
        setSelectedLayoutId(active.id);
        setNome(active.nome);
        setRows(active.layout || []);
      }
    } catch { /* ignore */ }
  };

  const loadLayout = (layout) => {
    setSelectedLayoutId(layout.id);
    setNome(layout.nome);
    setRows(layout.layout || []);
  };

  const nuovoLayout = () => {
    setSelectedLayoutId(null);
    setNome("Nuovo Layout");
    setRows([
      { tipo: "ricavi", nome: "Ricavi Netti", codici: ["5"] },
      { tipo: "gruppo", nome: "Costo del Personale", codici: ["701", "702"], segno: "-" },
      { tipo: "margine", nome: "Margine Operativo Lordo" },
      { tipo: "gruppo", nome: "Servizi e Consulenze", codici: ["705"], segno: "-" },
      { tipo: "margine", nome: "Margine Netto" },
    ]);
  };

  const addRow = (tipo) => {
    if (tipo === "ricavi") {
      setRows([...rows, { tipo: "ricavi", nome: "Ricavi", codici: ["5"] }]);
    } else if (tipo === "gruppo") {
      setRows([...rows, { tipo: "gruppo", nome: "Nuovo Gruppo Costi", codici: [], segno: "-" }]);
    } else {
      setRows([...rows, { tipo: "margine", nome: "Margine Intermedio" }]);
    }
  };

  const updateRow = (idx, field, value) => {
    const newRows = [...rows];
    newRows[idx] = { ...newRows[idx], [field]: value };
    setRows(newRows);
  };

  const removeRow = (idx) => setRows(rows.filter((_, i) => i !== idx));

  const moveRow = (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= rows.length) return;
    const newRows = [...rows];
    [newRows[idx], newRows[ni]] = [newRows[ni], newRows[idx]];
    setRows(newRows);
  };

  const handleSave = async (attivo = false) => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const payload = { nome, layout: rows, attivo };
      if (selectedLayoutId) {
        await api.put(`${prefix}/bilancio-layouts/${selectedLayoutId}`, payload);
      } else {
        const res = await api.post(`${prefix}/bilancio-layouts`, payload);
        setSelectedLayoutId(res.data.id);
      }
      toast.success(attivo ? "Layout salvato e attivato" : "Layout salvato");
      fetchLayouts();
      if (onSaved) onSaved();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedLayoutId) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/bilancio-layouts/${selectedLayoutId}`);
      toast.success("Layout eliminato");
      setSelectedLayoutId(null);
      setNome("");
      setRows([]);
      fetchLayouts();
      if (onSaved) onSaved();
    } catch {
      toast.error("Errore");
    }
  };

  const contiPrefissi = getCodiciPrefisso(contiDisponibili);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="layout-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-indigo-600" />
            Configura Margini Personalizzati
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {layouts.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Label className="text-xs font-semibold text-muted-foreground">Layout salvati:</Label>
              {layouts.map(l => (
                <Button
                  key={l.id}
                  variant={selectedLayoutId === l.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => loadLayout(l)}
                  className="gap-1"
                >
                  {l.nome}
                  {l.attivo && <Badge variant="secondary" className="text-[9px] ml-1">ATTIVO</Badge>}
                </Button>
              ))}
              <Button variant="outline" size="sm" onClick={nuovoLayout} className="gap-1">
                <Plus className="w-3 h-3" /> Nuovo
              </Button>
            </div>
          )}

          {layouts.length === 0 && (
            <Button variant="outline" onClick={nuovoLayout} className="w-full gap-2" data-testid="create-first-layout">
              <Plus className="w-4 h-4" /> Crea il tuo primo layout personalizzato
            </Button>
          )}

          {rows.length > 0 && (
            <>
              <div>
                <Label className="text-xs font-semibold">Nome Layout</Label>
                <Input value={nome} onChange={e => setNome(e.target.value)} placeholder="Es: Conto Economico a Scalare" data-testid="layout-name-input" />
              </div>

              <div className="space-y-2" data-testid="layout-rows">
                {rows.map((row, idx) => (
                  <LayoutRowEditor
                    key={`row-${row.tipo}-${idx}`}
                    row={row}
                    idx={idx}
                    total={rows.length}
                    contiPrefissi={contiPrefissi}
                    contiDisponibili={contiDisponibili}
                    categorieDisponibili={categorieDisponibili}
                    onChange={(field, val) => updateRow(idx, field, val)}
                    onRemove={() => removeRow(idx)}
                    onMove={(dir) => moveRow(idx, dir)}
                  />
                ))}
              </div>

              <div className="flex gap-2 pt-2 border-t">
                <Button variant="outline" size="sm" onClick={() => addRow("ricavi")} className="gap-1 text-green-700" data-testid="add-ricavi-row">
                  <Plus className="w-3 h-3" /> Ricavi
                </Button>
                <Button variant="outline" size="sm" onClick={() => addRow("gruppo")} className="gap-1 text-red-700" data-testid="add-gruppo-row">
                  <Plus className="w-3 h-3" /> Gruppo Costi
                </Button>
                <Button variant="outline" size="sm" onClick={() => addRow("margine")} className="gap-1 text-blue-700" data-testid="add-margine-row">
                  <Plus className="w-3 h-3" /> Margine
                </Button>
              </div>

              <LayoutPreview rows={rows} />
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          {selectedLayoutId && (
            <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-layout-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Elimina
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          {rows.length > 0 && (
            <>
              <Button variant="secondary" onClick={() => handleSave(false)} disabled={loading} data-testid="save-layout-btn">
                <Save className="w-4 h-4 mr-1" /> Salva
              </Button>
              <Button onClick={() => handleSave(true)} disabled={loading} data-testid="save-activate-layout-btn">
                <Save className="w-4 h-4 mr-1" /> Salva e Attiva
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
